import { useEffect, useMemo } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from "react-query";
import chunk from "lodash/chunk";
import uniq from "lodash/uniq";

import {
  AllFilterableColumnsDocument,
  FilterableColumnFragmentDoc,
  type AllFilterableColumnsQuery,
  type AllFilterableColumnsQueryVariables,
} from "src/graphql/types";
import { fetcher } from "src/utils/fetcher";
import { captureException } from "src/lib/monitoring";
import type { FilterableColumn } from "src/types/visual";

const EMPTY_OBJECT: Record<string, FilterableColumn[]> = {};

// Must stay in sync with MAX_FILTERABLE_COLUMNS_SEGMENT_IDS on the backend.
export const FILTERABLE_COLUMNS_BATCH_SIZE = 100;

type FilterableAudienceColumnsForSegmentIdsQuery = {
  filterableAudienceColumnsForSegmentIds: Array<{
    segmentId: string;
    columns: FilterableColumn[];
  }>;
};

type FilterableAudienceColumnsForSegmentIdsQueryVariables = {
  segmentIds: string[];
};

const FilterableAudienceColumnsForSegmentIdsDocument = `
  query FilterableAudienceColumnsForSegmentIds($segmentIds: [bigint!]!) {
    filterableAudienceColumnsForSegmentIds(segmentIds: $segmentIds) {
      segmentId
      columns {
        ...FilterableColumn
      }
    }
  }
  ${FilterableColumnFragmentDoc}
`;

type UseFetchFilterableAudienceColumnsReturn = {
  filterableColumns: Record<string, FilterableColumn[]>;
  isLoading: boolean;
  isError: boolean;
};

function filterableColumnsBySegmentId(
  entries:
    | ReadonlyArray<{ segmentId: string; columns: FilterableColumn[] }>
    | undefined,
): Record<string, FilterableColumn[]> | undefined {
  if (!entries) {
    return undefined;
  }

  const result: Record<string, FilterableColumn[]> = {};
  for (const { segmentId, columns } of entries) {
    result[segmentId] = columns;
  }
  return result;
}

function buildFilterableColumnsMapFromHasura(
  data: AllFilterableColumnsQuery | undefined,
): Record<string, FilterableColumn[]> | undefined {
  return filterableColumnsBySegmentId(
    data?.segments.map((segment) => ({
      segmentId: String(segment.id),
      columns: segment.filterable_audience_columns,
    })),
  );
}

function buildFilterableColumnsMapFromBatch(
  data: FilterableAudienceColumnsForSegmentIdsQuery | undefined,
): Record<string, FilterableColumn[]> | undefined {
  return filterableColumnsBySegmentId(
    data?.filterableAudienceColumnsForSegmentIds.map((entry) => ({
      segmentId: String(entry.segmentId),
      columns: entry.columns,
    })),
  );
}

export async function fetchBatchedFilterableColumns(
  segmentIds: string[],
  context?: QueryFunctionContext,
): Promise<Record<string, FilterableColumn[]>> {
  if (segmentIds.length === 0) {
    return EMPTY_OBJECT;
  }

  const idChunks = chunk(segmentIds, FILTERABLE_COLUMNS_BATCH_SIZE);
  // Chunks run in parallel via Promise.all: one failed chunk rejects the whole
  // fetch rather than returning a partial merge. React Query surfaces that as an
  // error; the hook still falls back to EMPTY_OBJECT until callers handle isError.
  const chunkResults = await Promise.all(
    idChunks.map((segmentIdsChunk) =>
      fetcher<
        FilterableAudienceColumnsForSegmentIdsQuery,
        FilterableAudienceColumnsForSegmentIdsQueryVariables
      >(FilterableAudienceColumnsForSegmentIdsDocument, {
        segmentIds: segmentIdsChunk,
      })(context),
    ),
  );

  const mergedEntries = chunkResults.flatMap(
    (data) => data.filterableAudienceColumnsForSegmentIds ?? [],
  );

  return (
    buildFilterableColumnsMapFromBatch({
      filterableAudienceColumnsForSegmentIds: mergedEntries,
    }) ?? EMPTY_OBJECT
  );
}

// Dedupes Sentry reports across hook instances and refetches — several
// components mount this hook with overlapping segment ids, and a missing id
// would otherwise be reported once per mount.
const reportedMissingSegmentIds = new Set<string>();

function logMissingFilterableSegmentIds({
  requestedSegmentIds,
  filterableColumns,
  batchPath,
}: {
  requestedSegmentIds: string[];
  filterableColumns: Record<string, FilterableColumn[]> | undefined;
  batchPath: boolean;
}) {
  if (!filterableColumns) {
    return;
  }

  const returned = new Set(Object.keys(filterableColumns));
  const missingSegmentIds = requestedSegmentIds.filter(
    (id) => !returned.has(id) && !reportedMissingSegmentIds.has(id),
  );

  if (missingSegmentIds.length === 0) {
    return;
  }

  for (const id of missingSegmentIds) {
    reportedMissingSegmentIds.add(id);
  }

  captureException(
    new Error("Filterable columns response missing requested segment IDs"),
    {
      tags: { feature: "filterableAudienceColumns" },
      extra: {
        missingSegmentIds,
        requestedCount: requestedSegmentIds.length,
        returnedCount: returned.size,
        batchPath,
      },
    },
  );
}

/**
 * Fetches filterable audience columns for a list of segments, grouped by model id.
 */
export const useFetchFilterableAudienceColumns = (
  segmentIds: string[],
  options?: {
    enabled?: boolean;
  },
): UseFetchFilterableAudienceColumnsReturn => {
  const { appBatchFilterableAudienceColumns } = useFlags();
  const useBatchPath = Boolean(appBatchFilterableAudienceColumns);

  // Guard against ids that were stringified from nullish values upstream —
  // String(undefined) is the truthy string "undefined" and would otherwise be
  // sent to the API as a segment id.
  const requestedSegmentIds = useMemo(
    () =>
      uniq(
        segmentIds
          .filter(
            (id) =>
              id != null && id !== "" && id !== "undefined" && id !== "null",
          )
          .map(String),
      ),
    [segmentIds],
  );

  const enabled = (options?.enabled ?? true) && requestedSegmentIds.length > 0;

  const hasuraQuery = useQuery<
    AllFilterableColumnsQuery,
    Error,
    Record<string, FilterableColumn[]>
  >(
    ["AllFilterableColumns", { segmentIds: requestedSegmentIds }],
    fetcher<AllFilterableColumnsQuery, AllFilterableColumnsQueryVariables>(
      AllFilterableColumnsDocument,
      {
        segmentIds: requestedSegmentIds,
      },
    ),
    {
      enabled: enabled && !useBatchPath,
      select: buildFilterableColumnsMapFromHasura,
    } as UseQueryOptions<
      AllFilterableColumnsQuery,
      Error,
      Record<string, FilterableColumn[]>
    >,
  );

  const batchQuery = useQuery<
    Record<string, FilterableColumn[]>,
    Error,
    Record<string, FilterableColumn[]>
  >(
    [
      "FilterableAudienceColumnsForSegmentIds",
      { segmentIds: requestedSegmentIds },
    ],
    (context) => fetchBatchedFilterableColumns(requestedSegmentIds, context),
    {
      enabled: enabled && useBatchPath,
    } as UseQueryOptions<
      Record<string, FilterableColumn[]>,
      Error,
      Record<string, FilterableColumn[]>
    >,
  );

  const activeQuery = useBatchPath ? batchQuery : hasuraQuery;

  const filterableColumns = activeQuery.data ?? EMPTY_OBJECT;
  const isLoading = activeQuery.isLoading;

  useEffect(() => {
    if (isLoading || activeQuery.isError || !enabled) {
      return;
    }

    logMissingFilterableSegmentIds({
      requestedSegmentIds,
      filterableColumns: activeQuery.data,
      batchPath: useBatchPath,
    });
  }, [
    activeQuery.data,
    activeQuery.isError,
    useBatchPath,
    enabled,
    isLoading,
    requestedSegmentIds,
  ]);

  return {
    filterableColumns,
    isLoading,
    isError: activeQuery.isError,
  };
};
